/**
 * Where the money collected in Stripe came from, each payment counted once.
 *
 * Revenue is credited to the visitor's first-touch view and counted on payment
 * date (Humblytics MCP `get_ads_attribution` definitions). So a subscription
 * bought from a July campaign pays again every month and lands on that July
 * campaign each time, even after the campaign ended. Renewals are split out
 * once payments carry Stripe's invoice `billing_reason`
 * ("subscription_create" for a first payment, "subscription_cycle" for a
 * renewal); until then the renewal part is null and stays hidden.
 * Contract: docs/TEMP_ATTRIBUTION_RENEWALS_CONTRACT.md.
 */

import { toCents, type RevenueTransaction } from "@/lib/utils/revenueUtils"

export type CollectedSplit = {
  collected: number
  /** First payments (or every payment while renewals are unknown) traced to an ad click. */
  fromAds: number
  /** Renewals credited to a campaign the subscription first came from. Null: not known yet. */
  renewals: number | null
  /** Matched to a visit that was not an ad click: organic, direct, referral, email. */
  otherVisits: number
  /** Not matched to any visit. */
  notMatched: number
}

/** Renewal dollars among attributed payments that name a campaign, or null when no payment says. */
export function renewalDollars(attributed: RevenueTransaction[]): number | null {
  // The backend sends the key on every row once it ships (null on one-off payments).
  const known = attributed.some((t) => "billing_reason" in t)
  if (!known) return null
  const cents = attributed
    .filter((t) => t.billing_reason === "subscription_cycle" && String(t.utm_campaign ?? "").trim() !== "")
    .reduce((sum, t) => sum + toCents(t), 0)
  return cents / 100
}

/**
 * Renewal dollars per campaign, keyed by lowercase utm_campaign (the
 * CampaignsTable row key). Null while payments don't say which ones renew.
 */
export function renewalsByCampaign(attributed: RevenueTransaction[]): Map<string, number> | null {
  if (!attributed.some((t) => "billing_reason" in t)) return null
  const map = new Map<string, number>()
  for (const t of attributed) {
    const key = String(t.utm_campaign ?? "").trim().toLowerCase()
    if (t.billing_reason !== "subscription_cycle" || !key) continue
    map.set(key, (map.get(key) ?? 0) + toCents(t) / 100)
  }
  return map
}

export function collectedSplit(input: {
  collected: number
  /** Matched to any visit. */
  attributed: number
  /** Matched to an ad campaign (backend-verified). */
  ads: number
  renewals: number | null
}): CollectedSplit {
  const collected = Math.max(0, input.collected)
  const ads = Math.min(Math.max(0, input.ads), collected)
  const renewals = input.renewals == null ? null : Math.min(Math.max(0, input.renewals), ads)
  const attributed = Math.min(Math.max(input.attributed, ads), collected)
  return {
    collected,
    fromAds: ads - (renewals ?? 0),
    renewals,
    otherVisits: attributed - ads,
    notMatched: collected - attributed,
  }
}
